import { CircleAlert } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';

/**
 * Red exclamation shown in the admin list tables when the customer is waiting
 * on us — their message (or their unanswered submission) is the last word.
 *
 * Renders nothing when nothing is owed, so the column stays quiet and a row
 * that needs attention is the only thing that draws the eye. The cell keeps its
 * width either way, which is what makes the column scannable.
 */
export function AwaitingReplyFlag({
  awaiting,
  className,
}: {
  awaiting?: boolean;
  className?: string;
}) {
  if (!awaiting) return null;
  return (
    <span
      role="img"
      aria-label="Awaiting reply"
      title="Awaiting reply — the customer had the last word"
      className={cn('inline-flex', className)}
    >
      <CircleAlert className="h-4 w-4 text-destructive" aria-hidden="true" />
    </span>
  );
}

/**
 * The manual override for the flag above: an admin deliberately leaving the
 * customer's remarks as the last word ticks this, and the row stops flagging.
 *
 * On tickets the waiver is time-anchored server-side — if the customer writes
 * again the flag returns, which is why the label says "for now".
 */
export function ReplyWaivedCheckbox({
  waived,
  onChange,
  disabled,
  className,
}: {
  waived: boolean;
  onChange: (waived: boolean) => void;
  disabled?: boolean;
  className?: string;
}) {
  return (
    <label
      className={cn(
        'flex items-center gap-2 text-sm',
        disabled ? 'cursor-default opacity-70' : 'cursor-pointer',
        className,
      )}
    >
      <Checkbox
        checked={waived}
        disabled={disabled}
        onCheckedChange={(checked) => onChange(checked === true)}
        aria-label="No reply needed"
      />
      <span className={waived ? 'font-medium' : 'text-muted-foreground'}>No reply needed</span>
    </label>
  );
}
